import React, { useState } from 'react';
import { extra, text } from '@/theme/colors/palettes';
import { HStack, Image, Text, VStack } from '@chakra-ui/react';
import CustomButton from '@/components/atoms/CustomButton';
import { employeeUserRoutes } from '@/constants/routes/app';
import { useNavigate } from '@tanstack/react-router';
import Calendly from '@/components/molecules/Calendly';
import useOrganizationMemberCares from '@/hooks/membercare/useOrganizationMemberCares';
import MemberCareCircledIcon from '@/components/atoms/MemberCareCircledIcon';
import svgIcons from '@/assets/svg/icons';
import { cutStringOverflow } from '@/utils/stringUtils';

const IMG_SIZE = 92;
const BIO_TOGGLE_THRESHOLD = 320;

const DiscussWithMemberCareCard = () => {
  const navigate = useNavigate({ from: employeeUserRoutes.userDashboard });

  const { data: memberCares } = useOrganizationMemberCares();

  const hasJustOneMemberCare = memberCares?.length === 1;
  const firstMemberCare = memberCares?.[0];
  const memberCareBio = firstMemberCare?.bio ?? '';
  const normalizedBio = memberCareBio.trimEnd();
  const shouldShowBioToggle = normalizedBio.length > BIO_TOGGLE_THRESHOLD;
  const collapsedBio = cutStringOverflow({
    str: normalizedBio,
    length: BIO_TOGGLE_THRESHOLD,
    overflowSign: '...',
  });

  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);
  const [isBioExpanded, setIsBioExpanded] = useState(false);

  return (
    <VStack
      backgroundColor={extra.white}
      borderRadius={'10px'}
      boxShadow="0px 8px 40px 0px #0000001A"
      padding={'20px'}
      // minWidth={'400px'}
      maxWidth={'400px'}
      alignItems={'start'}
      gap={'10px'}
    >
      <Text variant={'loraMedium18'} color={text.darkBlue}>
        Discuss these Results with Member Care
      </Text>
      <HStack alignItems={'start'} gap={'20px'}>
        {hasJustOneMemberCare ? (
          <Image
            src={firstMemberCare?.profilePicture}
            alt="member-care-avatar"
            cursor={firstMemberCare?.user_id ? 'pointer' : 'default'}
            onClick={() => {
              if (!firstMemberCare?.user_id) return;

              navigate({
                to: '/directory/member-care/$memberCareId',
                params: { memberCareId: firstMemberCare.user_id },
              });
            }}
            style={{
              width: IMG_SIZE,
              height: IMG_SIZE,
              margin: '2px',
              borderRadius: '50%',
              padding: '2px',
              backgroundColor: 'white',
            }}
            objectFit={'cover'}
            boxShadow="0px 4px 16px 0px #00417933"
          />
        ) : (
          <MemberCareCircledIcon size={IMG_SIZE} />
        )}
        {hasJustOneMemberCare ? (
          <VStack alignItems={'start'} gap={0}>
            <Text variant={'lora'} fontSize={14} lineHeight={'20px'} fontWeight={'bold'} color={text.mediumGray}>
              Member Care Specialist
            </Text>
            <Text variant={'loraMedium18'} lineHeight={'20px'} color={text.darkBlue} marginBottom={'10px'}>
              {firstMemberCare.firstName} {firstMemberCare.lastName}
            </Text>
            <CustomButton
              label="Contact"
              onClick={() => setIsCalendlyOpen(true)}
              backgroundColor={extra.white}
              hoverOpacityDrop
              noShadow
              labelColor="text.mediumBlue"
              style={{
                padding: 0,
                borderColor: text.mediumBlue,
                borderWidth: '2px',
              }}
              width={'180px'}
              height={'44px'}
            />

            <Calendly isOpen={isCalendlyOpen} setIsOpen={setIsCalendlyOpen} url={firstMemberCare.calendly_link} />
          </VStack>
        ) : (
          <VStack alignItems={'start'} gap={2} justifyContent={'center'} height={IMG_SIZE}>
            <VStack alignItems={'start'} gap={0}>
              <Text variant={'urbanistSemiBold'} color={text.darkBlue}>
                Your organization has
              </Text>
              <Text variant={'urbanistSemiBold'} color={text.darkBlue}>
                {memberCares?.length} Member Care Specialists
              </Text>
            </VStack>
            <CustomButton
              label="Member Care"
              hoverOpacityDrop
              noShadow
              iconAfterLabelSvgPath={svgIcons.rightArrow}
              iconAfterLabelSvgColor={text.mediumBlue}
              iconAfterLabelSvgSize={11}
              iconAfterLabelSvgBoxHeight={14}
              iconAfterLabelSvgBoxWidth={14}
              onClick={() => navigate({ to: '/directory/member-care' })}
              backgroundColor={extra.white}
              labelColor="text.mediumBlue"
              style={{
                padding: 0,
                borderColor: text.mediumBlue,
                borderWidth: '2px',
              }}
              width={'180px'}
              height={'46px'}
            />
          </VStack>
        )}
      </HStack>

      {/* BIO / ABOUT MYOMNIA MEMBER CARE */}
      {hasJustOneMemberCare ? (
        <VStack alignItems={'flex-start'} width={'100%'} gap={'4px'} marginTop={'10px'}>
          <Text variant={'urbanistExtraBoldSmall'} color={'text.mediumGray'}>
            BIO
          </Text>
          <Text
            height={'100%'}
            width={'100%'}
            whiteSpace={'pre-wrap'}
            variant={'urbanistSemiBoldRegular'}
            color={'text.mediumBlue'}
          >
            {isBioExpanded || !shouldShowBioToggle ? normalizedBio : collapsedBio}
          </Text>
          {shouldShowBioToggle && (
            <Text
              variant={'urbanistExtraBoldSmall'}
              color={'text.mediumGray'}
              cursor={'pointer'}
              onClick={() => setIsBioExpanded((prev) => !prev)}
            >
              {isBioExpanded ? 'READ LESS ↑' : 'READ MORE ↓'}
            </Text>
          )}
        </VStack>
      ) : (
        <VStack alignItems={'flex-start'} width={'100%'} gap={'4px'} marginTop={'20px'}>
          <Text variant={'urbanistExtraBoldSmall'} color={'text.mediumGray'}>
            ABOUT MYOMNIA MEMBER CARE
          </Text>
          <Text
            height={'100%'}
            whiteSpace={'pre-wrap'}
            variant={'urbanistSemiBoldRegular'}
            color={'text.mediumBlue'}
            noOfLines={8}
          >
            A dedicated group of professionals who support members’ overall wholeness—mental, emotional, physical,
            social, and purpose-driven. They guide, listen, and connect members with the right resources to help them
            feel supported, balanced, and cared for at every step.
          </Text>
        </VStack>
      )}
    </VStack>
  );
};

export default DiscussWithMemberCareCard;
